import React from "react";
import { createPortal } from "react-dom";
import peopleIcon from "../../../../../assets/icons/people.svg";

// ResourceDetailModal Component
const ResourceDetailPanel = ({
  resource,
  isOpen,
  onClose,
}: {
  resource: any;
  isOpen: boolean;
  onClose: () => void;
}) => {
  if (!resource) return null;

  return createPortal(
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black z-[9998] transition-opacity duration-300 ${
          isOpen ? "bg-opacity-30" : "bg-opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full bg-white w-full max-w-md z-[9999] shadow-2xl transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        } dark:bg-darkBackground`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-softGray">
              {resource?.name || resource?.Name || "Resource Details"}
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:text-softGray dark:hover:text-darkGray"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4 overflow-y-auto flex-1">
            {resource?._isLoadingDetails ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[300px]">
                <div className="animate-spin rounded-full h-12 w-12 border-[3px] border-blue-600 border-t-transparent mb-4"></div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600 dark:text-softGray">Status</span>
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        resource?.isAvailable !== false ? "bg-green-500" : "bg-red-500"
                      }`}
                    />
                    <span
                      className={`text-sm px-2 py-1 rounded-full text-xs font-medium ${
                        resource?.isAvailable !== false ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                      }`}
                    >
                      {resource?.isAvailable !== false ? "Available" : "Unavailable"}
                    </span>
                  </div>
                </div>

                {/* Resource Type */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600 dark:text-softGray">Resource type</span>
                  <span className="text-sm text-blue-600 font-medium dark:text-softGray">{resource?.type}</span>
                </div>

                {/* Location */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600 dark:text-softGray">Location</span>
                  <span className="text-sm text-gray-900 text-right flex-1 ml-4 dark:text-softGray">
                    {resource?.locationPath || resource?.Location || "BaDinh/Hanoi"}
                  </span>
                </div>

                {/* Capacity */}
                {(resource?.capacity || resource?.Capacity) && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600 dark:text-softGray">Capacity</span>
                    <span className="text-sm text-gray-900 dark:text-softGray flex items-center gap-1">
                      {resource?.capacity || resource?.Capacity}
                      <img src={peopleIcon} alt="capacity" className="w-4 h-4 flex-shrink-0" />
                    </span>
                  </div>
                )}

                {/* License Type */}
                {resource?.licenseType && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600 dark:text-softGray">License type</span>
                    <span className="text-sm text-gray-900 dark:text-softGray">{resource.licenseType}</span>
                  </div>
                )}

                {/* Categories */}
                {resource?.categories?.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-sm font-medium text-gray-600 dark:text-softGray">Categories</span>
                    <div className="flex flex-wrap items-center gap-2">
                      {resource.categories.map((category: string, index: number) => (
                        <span
                          key={index}
                          className="px-2.5 py-1 text-xs font-medium text-indigo-800 bg-indigo-100 rounded-full"
                        >
                          {category}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description */}
                {(resource?.description || resource?.Description) && (
                  <div className="space-y-2">
                    <span className="text-sm font-medium text-gray-600 dark:text-softGray">Description</span>
                    <div className="text-sm text-gray-900 text-right dark:text-softGray">
                      {resource?.description || resource?.Description}
                    </div>
                  </div>
                )}

                {/* Resource Email */}
                {(resource?.email || resource?.Email) && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600 dark:text-softGray">Resource mail</span>
                    <span className="text-sm text-gray-900 text-right flex-1 ml-4 dark:text-softGray">
                      {resource?.email || resource?.Email}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ResourceDetailPanel;
